import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'

import $api from '../services/api'

Vue.use(Vuex)

const getInitialState = () => {
    return {
        steps: [],
        step: '',
        ticket: 'oneway',
        multicity: false,
        legs: {
            out: {
                origin: null,
                destination: null,
                trip: null,
                date: null,
            },
            ret: {
                origin: null,
                destination: null,
                trip: null,
                date: null,
            },
        },
        specialOffer: false,
        accommodationClass: '',
        passengersCount: {
            adl: 0,
            chd: 0,
            inf: 0,
        },
        passengers: [],
        leadPassenger: 0,
        supplements: [],
        luggage: [],
        transfer: null,
        email: '',
        phone: '',
        billing: {},
        quote: null,
        quoteLoading: false,
        prices: null,
        termsAccepted: false,
        privacyAccepted: false,
        covidInfoAccepted: false,
        newsletter: false,
        luggageInfo: {
            free: 0,
            extra: false,
        },
        processing: false,
        apiError: '',
        validationErrors: [],
        availableSupplements: [],
        visaRules: [],
    }
}

const getBlankPassenger = () => {
    return {
        name: '',
        surname: '',
        gender: '',
        dateOfBirth: '',
        nationality: '',
        documentType: '',
        documentNumber: '',
        assistance: false,
        notes: '',
        hasVisa: '',
    }
}

export default {
    namespaced: true,
    state: getInitialState(),
    getters: {
        getDeparture: (state) => (direction) => {
            if (!state.legs[direction].trip) {
                return null
            }

            return moment(state.legs[direction].trip.itinerary.find(leg => leg.origin_id == state.legs[direction].origin).departure)
        },
        getArrival: (state) => (direction) => {
            if (!state.legs[direction].trip) {
                return null
            }

            return moment(state.legs[direction].trip.itinerary.find(leg => leg.destination_id == state.legs[direction].destination).arrival)
        },
    },
    mutations: {
        setSteps(state, steps) {
            state.steps = steps
        },
        setStep(state, currentStep) {
            if (state.steps.find(step => step.name == currentStep)) {
                state.step = currentStep
            } else {
                state.step = ''
            }
        },
        setTicket(state, payload) {
            state.ticket = payload.ticket
            state.multicity = (payload.multicity == true)
        },
        setRoute(state, payload) {
            state.legs[payload.leg].origin = payload.origin
            state.legs[payload.leg].destination = payload.destination
        },
        setTrip(state, payload) {
            if (!payload.trip) {
                state.quote = null
            }
            state.legs[payload.leg].trip = payload.trip
            if (payload.accommodationClass) {
                state.accommodationClass = payload.accommodationClass
            }
            if (payload.specialOffer !== undefined) {
                state.specialOffer = payload.specialOffer
            }
        },
        resetState(state) {
            const initial = getInitialState()
            Object.keys(initial).forEach(key => { state[key] = initial[key] })
        },
        setPassengersCount(state, payload) {
            state.passengersCount = payload
            
            let total = Object.values(payload).reduce((a, b) => a + b, 0)
            if (total > state.passengers.length) {
                let diff = total - state.passengers.length
                for (let i = 0; i < diff; i ++) {
                    state.passengers.push(getBlankPassenger())
                }
            }
            else if (total < state.passengers.length) {
                state.passengers = state.passengers.slice(0, total)
            }
            state.passengers.map((passenger, index) => {
                if (index < state.passengersCount.adl) {
                    passenger.category = 'adl'
                }
                else if (index < (state.passengersCount.adl + state.passengersCount.chd)) {
                    passenger.category = 'chd'
                }
                else {
                    passenger.category = 'inf'
                }
            })
        },
        setPassengerDetails(state, payload) {
            state.passengers.splice(payload.index, 1, payload.passenger)
        },
        setDate(state, payload) {
            state.legs[payload.leg].date = payload.date
            if (state.ticket != 'oneway' && payload.leg == 'out') {
                let outDate = moment(state.legs.out.date)
                let retDate = moment(state.legs.ret.date)
                if (state.ticket == 'daytrip' && outDate != retDate) {
                    state.legs['ret'].date = payload.date
                }
                else if (state.ticket == 'return' && outDate.isSameOrAfter(retDate)) {
                    state.legs['ret'].date = outDate.add(1, 'days').format('YYYY-MM-DD')
                }
            }
        },
        setLeadPassenger(state, payload) {
            state.leadPassenger = payload
        },
        setTermsAcceptedStatus(state, payload) {
            state.termsAccepted = payload
        },
        setPrivacyAcceptedStatus(state, payload) {
            state.privacyAccepted = payload
        },
        setCovidInfoAcceptedStatus(state, payload) {
            state.covidInfoAccepted = payload
        },
        setNewsletterStatus(state, payload) {
            state.newsletter = payload
        },
        setEmail(state, payload) {
            state.email = payload
        },
        setPhone(state, payload) {
            state.phone = payload
        },
        addSupplement(state, payload) {
            let itemIndex = state.supplements.findIndex(supplement => supplement.passenger == payload.passenger && supplement.id == payload.supplement)

            let supplementItem = (itemIndex >= 0) ? state.supplements[itemIndex] : {
                passenger: payload.passenger,
                id: payload.supplement,
            }

            supplementItem.quantity = parseInt(payload.quantity) || 1

            if (itemIndex > -1) {
                state.supplements.splice(itemIndex, 1, supplementItem)
            } else {
                state.supplements.push(supplementItem)
            }
        },
        updateSupplementQuantity(state, payload) {
            state.supplements = state.supplements.map(supplement => {
                if (payload.passenger == supplement.passenger && payload.supplement == supplement.id) {
                    return {...supplement, quantity: parseInt(payload.quantity) || 1}
                }
                return supplement
            })
        },
        removeSupplement(state, payload) {
            state.supplements = state.supplements.filter(supplement => !(supplement.id == payload.supplement && supplement.passenger == payload.passenger))
        },
        removeSupplements(state) {
            state.supplements = []
        },
        addLuggage(state, payload) {
            let luggage = state.luggage.find(luggage => luggage.passenger == payload.passenger)
            if (!luggage) {
                luggage = {
                    passenger: payload.passenger
                }
            }
            luggage.quantity = payload.quantity
            state.luggage.push(luggage)
        },
        updateLuggageQuantity(state, payload) {
            let index = state.luggage.findIndex(luggage => luggage.passenger == payload.passenger)
            if (index > -1) {
                let luggage = state.luggage[index]
                state.luggage.splice(index, 1, {...luggage, quantity: payload.quantity})
            }
        },
        removeLuggage(state, payload) {
            if (payload === undefined || payload === null) {
                state.luggage = []
            } else {
                state.luggage = state.luggage.filter(luggage => luggage.passenger != payload.passenger)
            }
        },
        setLuggageInfo(state, payload) {
            state.luggageInfo.extra = !!payload.extra
            state.luggageInfo.free = parseInt(payload.free)
        },
        startProcessing(state) {
            state.processing = true
        },
        stopProcessing(state) {
            state.processing = false
        },
        setQuote(state, payload) {
            state.quote = payload
            state.quoteLoading = false
        },
        setPrices(state, payload) {
            state.prices = payload
        },
        setAvailableSupplements(state, payload) {
            state.availableSupplements = payload
        },
        setValidationErrors(state, payload) {
            state.validationErrors = payload
        },
        setVisaRules(state, payload) {
            state.visaRules = payload
        },
        setApiError(state, payload) {
            state.apiError = payload
        },
        setTransfer(state, payload) {
            state.transfer = payload
        }
    },
    actions: {
        setSteps({ state, commit }) {
            let steps = [
                { name: 'trip', label: 'Trip details' },
                { name: 'passengers', label: 'Passengers details' },
                { name: 'supplements', label: 'Supplements' },
            ]

            if (state.ticket == 'daytrip') {
                steps.push({ name: 'transfers', label: 'Transfer' })
            }

            steps.push({ name: 'payment', label: 'Payment' })
            steps.push({ name: 'confirmation', label: 'Confirmation' })

            commit('setSteps', steps)
        },
        nextStep(context) {
            let currentStepIndex = context.state.steps.findIndex(step => context.state.step == step.name)
            context.commit('setStep', context.state.steps[currentStepIndex + 1].name)
        },
        previousStep(context) {
            let currentStepIndex = context.state.steps.findIndex(step => context.state.step == step.name)
            context.commit('setStep', context.state.steps[currentStepIndex - 1].name)
        },
        reset({ commit }) {
            commit('resetState')
        },
        async getSupplements({ commit }) {
            return $api.supplements().then(response => commit('setAvailableSupplements', response))
        },
        async getLuggageInfo({ state, commit }) {
            let info = await $api.luggageInfo({
                out_origin_id: state.legs.out.origin,
                out_destination_id: state.legs.out.destination,
                out_trip_id: state.legs.out.trip.id,
                class: state.accommodationClass,
                ...(state.ticket != 'oneway' && {
                    ret_origin_id: state.legs.ret.origin,
                    ret_destination_id: state.legs.ret.destination,
                    ret_trip_id: state.legs.ret.trip.id,
                })
            })

            commit('setLuggageInfo', info)
        },
        async getQuote({ state, commit }) {
            state.quoteLoading = true

            if (!state.legs.out.trip || (state.ticket != 'oneway' && !state.legs.ret.trip)) {
                commit('setQuote', null)
                return
            }

            let supplements = {}
            state.supplements.map(supplement => {
                if (!supplements[supplement.id]) {
                    supplements[supplement.id] = supplement.quantity
                } else {
                    supplements[supplement.id] += supplement.quantity
                }
            })
            $api.get('get_quote', {
                out_trip_id: state.legs.out.trip.id,
                out_origin_id: state.legs.out.origin,
                out_destination_id: state.legs.out.destination,
                ...(state.ticket != 'oneway' && {
                    ret_trip_id: state.legs.ret.trip.id,
                    ret_origin_id: state.legs.ret.origin,
                    ret_destination_id: state.legs.ret.destination,
                }),
                class: state.accommodationClass.toUpperCase(),
                passengers: {
                    ADL: state.passengersCount.adl,
                    CHD: state.passengersCount.chd,
                    INF: state.passengersCount.inf,
                },
                out_departure_transfer_id: state.transfer,
                ret_arrival_transfer_id: state.transfer,
                special_offer_id: state.specialOffer ? state.specialOffer.id : null,
                supplements: supplements,
                luggage: state.luggage.reduce((a, b) => a + b.quantity, 0),
            }).then(response => commit('setQuote', response.data))
        },
        async getPrices({ state, commit }) {
            let supplements = {}
            state.availableSupplements.map(supplement => {
                supplements[supplement.id] = 1
            })
            return $api.get('get_quote', {
                out_trip_id: state.legs.out.trip.id,
                out_origin_id: state.legs.out.origin,
                out_destination_id: state.legs.out.destination,
                ...(state.ticket != 'oneway' && {
                    ret_trip_id: state.legs.ret.trip.id,
                    ret_origin_id: state.legs.ret.origin,
                    ret_destination_id: state.legs.ret.destination,
                }),
                class: state.accommodationClass.toUpperCase(),
                passengers: {
                    ADL: state.passengersCount.adl,
                    CHD: state.passengersCount.chd,
                    INF: state.passengersCount.inf,
                },
                out_departure_transfer_id: state.transfer,
                ret_arrival_transfer_id: state.transfer,
                special_offer_id: state.specialOffer ? state.specialOffer.id : null,
                luggage: 1,
                supplements: supplements,
            }).then(response => commit('setPrices', response.data.prices))
        },
        async getVisaRules({ state, commit }) {
            $api.visaRules({
                out_origin_id: state.legs.out.origin,
                out_destination_id: state.legs.out.destination,
                ...(state.ticket != 'oneway' && {
                    ret_origin_id: state.legs.ret.origin,
                    ret_destination_id: state.legs.ret.destination,
                })
            }).then(result => commit('setVisaRules', result))
        },
        async startBooking({ state, commit, dispatch }) {
            commit('startProcessing')

            commit('setValidationErrors', [])
            
            $api.post('start_booking', {
                out_trip_id: state.legs.out.trip.id,
                out_origin_id: state.legs.out.origin,
                out_destination_id: state.legs.out.destination,
                ticket_type: state.ticket == 'oneway' ? 'ONEWAY' : 'RETURN',
                ...(state.ticket != 'oneway' && {
                    ret_trip_id: state.legs.ret.trip.id,
                    ret_origin_id: state.legs.ret.origin,
                    ret_destination_id: state.legs.ret.destination,
                }),
                class: state.accommodationClass.toUpperCase(),
                passengers_count: {
                    ADL: state.passengersCount.adl,
                    CHD: state.passengersCount.chd,
                    INF: state.passengersCount.inf,
                },
                special_offer_id: state.specialOffer ? state.specialOffer.id : null,
            }).then(response => {
                if (response.data.success) {
                    dispatch('getSupplements').then(() => {
                        let quote = dispatch('getQuote')
                        let prices = dispatch('getPrices')
                        let visaRules = dispatch('getVisaRules')
                        let luggageInfo = dispatch('getLuggageInfo')

                        Promise.all([quote, prices, visaRules, luggageInfo]).then(response => {
                            dispatch('nextStep')
                        }).catch(error => {
                            console.error('An unexpected error has occurred')
                            console.error(error)
                        }).finally(() => {
                            commit('stopProcessing')
                        })
                    })
                } else {
                    commit('setValidationErrors', Object.values(response.data.errors))
                    commit('stopProcessing')
                }
            }).catch(error => {
                console.error(error)
                commit('stopProcessing')

            })
        },
        async updateBooking({ state, commit, dispatch }, payload) {
            commit('startProcessing')

            commit('setValidationErrors', [])

            $api.post('update_booking', {
                ...(payload.includes('passengers') && {
                    passengers: state.passengers.map((passenger, index) => {
                        return {
                            lead: state.leadPassenger == index ? 1 : 0,
                            name: passenger.name,
                            surname: passenger.surname,
                            gender: passenger.gender,
                            date_of_birth: passenger.dateOfBirth ? moment(passenger.dateOfBirth).format('YYYY-MM-DD') : '',
                            nationality: passenger.nationality,
                            assistance_required: passenger.assistance,
                            notes: passenger.notes,
                            document_type: passenger.documentType,
                            document_number: passenger.documentNumber,
                        }
                    }),
                    contact: {
                        email: state.email,
                        phone: state.phone,
                        receive_newsletter: state.newsletter,
                    }
                }),
                ...(payload.includes('supplements') && {
                    supplements: state.supplements.length ? state.supplements.map(supplement => {
                        return {
                            passenger: supplement.passenger,
                            supplement_id: supplement.id,
                            quantity: supplement.quantity,
                        }
                    }) : ''
                }),
                ...(payload.includes('luggage') && {
                    luggage: state.luggage.length ? state.luggage.map(luggage => {
                        return {
                            passenger: luggage.passenger,
                            quantity: luggage.quantity,
                        }
                    }) : ''
                }),
                ...(payload.includes('transfers') && {
                    out_departure_transfer_id: state.transfer,
                    ...(state.ticket != 'oneway' && {
                        ret_arrival_transfer_id: state.transfer,
                    }),
                })
            }).then(response => {
                if (response.data.success) {
                    dispatch('getQuote')
                    dispatch('nextStep')
                } else if (response.data.data && response.data.data.expired) {
                    dispatch('restart')
                    commit('setApiError', response.data.data.message)
                } else if (response.data.errors) {
                    commit('setValidationErrors', Object.values(response.data.errors))
                }
            }).finally(() => commit('stopProcessing'))
        },
        restart({ state, commit, dispatch }) {
            let legs = JSON.parse(JSON.stringify(state.legs))
            let passengersCount = {...state.passengersCount}
            let ticket = state.ticket
            let multicity = state.multicity
            dispatch('reset')
            commit('setTicket', {
                ticket: ticket,
                multicity: multicity,
            })
            commit('setRoute', {
                origin: legs.out.origin,
                destination: legs.out.destination,
                leg: 'out',
            })
            commit('setDate', {
                date: legs.out.date,
                leg: 'out',
            })
            commit('setRoute', {
                origin: legs.ret.origin,
                destination: legs.ret.destination,
                leg: 'ret',
            })
            commit('setDate', {
                date: legs.ret.date,
                leg: 'ret',
            })
            commit('setPassengersCount', passengersCount)
            dispatch('setSteps')
            commit('setStep', 'trip')
        },
        setTransfer({ commit, dispatch }, payload) {
            commit('setTransfer', payload)
            dispatch('getQuote')
        }
    },
}